import { useEffect, useRef, useState } from "react";
import {
    adminImportClubCsv, adminGetClubVoucherSettings, adminSaveClubVoucherSettings,
} from "@/lib/api";

export const ClubImportCsvButton = ({ onDone }) => {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState("");

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        setBusy(true); setErr(""); setResult(null);
        try {
            const r = await adminImportClubCsv(f);
            setResult(r);
            onDone?.();
        } catch (ex) {
            const d = ex.response?.data?.detail;
            setErr(typeof d === "string" ? d : (d?.[0]?.msg || "Import failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex items-center gap-3 flex-wrap">
            <input ref={ref} type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={onFile} data-testid="club-import-file-input" />
            <button className="btn-secondary" disabled={busy} onClick={() => ref.current?.click()} data-testid="club-import-csv-btn">
                {busy ? "Importing…" : "⬆ Import Members (Excel or CSV)"}
            </button>
            {result && (
                <span className="font-mono text-[11px] text-gold" data-testid="club-import-summary">
                    {result.received} rows · {result.created} created · {result.updated} updated · {result.skipped} skipped
                </span>
            )}
            {err && <span className="font-mono text-[11px] text-[#ff5246]" data-testid="club-import-error">{err}</span>}
        </div>
    );
};

const RuleEditor = ({ label, rule, onChange, prefix }) => (
    <div className="border border-[var(--border)] p-3 flex-1 min-w-[240px] max-w-full overflow-hidden">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted mb-2">{label}</div>
        <label className="flex items-center gap-2 text-[13px] mb-2">
            <input type="checkbox" checked={rule.active} onChange={(e) => onChange({ ...rule, active: e.target.checked })} data-testid={`${prefix}-active`} />
            Active
        </label>
        <div className="flex flex-wrap gap-2">
            <select className="input !w-24" value={rule.type} onChange={(e) => onChange({ ...rule, type: e.target.value })} data-testid={`${prefix}-type`}>
                <option value="percent">% off</option>
                <option value="fixed">£ off</option>
            </select>
            <input className="input !w-20" type="number" min="0" step="0.5" value={rule.value} placeholder="Value"
                onChange={(e) => onChange({ ...rule, value: Number(e.target.value) })} data-testid={`${prefix}-value`} />
            <input className="input !w-20" type="number" min="0" step="0.5" value={rule.min_order} placeholder="Min £"
                onChange={(e) => onChange({ ...rule, min_order: Number(e.target.value) })} data-testid={`${prefix}-min`} />
        </div>
        <div className="font-mono text-[10px] text-muted mt-1">type · value · min order £</div>
        <label className="flex items-center gap-2 text-[13px] mt-2">
            <input type="checkbox" checked={!!rule.collection_only} onChange={(e) => onChange({ ...rule, collection_only: e.target.checked })} data-testid={`${prefix}-collection-only`} />
            Collection orders only
        </label>
    </div>
);

export const ClubVoucherSettings = () => {
    const [s, setS] = useState(null);
    const [msg, setMsg] = useState("");
    useEffect(() => { adminGetClubVoucherSettings().then(setS).catch(() => setMsg("Failed to load")); }, []);
    if (!s) return msg ? <p className="text-[#ff5246] text-sm">{msg}</p> : null;

    const save = async () => {
        setMsg("");
        try { setS(await adminSaveClubVoucherSettings(s)); setMsg("Saved"); }
        catch (ex) { setMsg(ex.response?.data?.detail?.[0]?.msg || "Save failed"); }
    };

    return (
        <div className="mb-5" data-testid="club-voucher-settings">
            <p className="eyebrow mb-2">// What Club codes allow (one use per code)</p>
            <div className="flex gap-3 flex-wrap">
                <RuleEditor label="Main voucher (NAAMI-xxxxx)" rule={s.main} prefix="club-main" onChange={(main) => setS({ ...s, main })} />
                <RuleEditor label="Bonus / referral codes" rule={s.bonus} prefix="club-bonus" onChange={(bonus) => setS({ ...s, bonus })} />
                <RuleEditor label="Standard member discount (auto, when no code used)" rule={s.member} prefix="club-member" onChange={(member) => setS({ ...s, member })} />
            </div>
            <div className="flex items-center gap-3 mt-2">
                <button className="btn-primary" onClick={save} data-testid="club-voucher-save">Save voucher rules</button>
                {msg && <span className="font-mono text-[11px] text-gold" data-testid="club-voucher-msg">{msg}</span>}
            </div>
        </div>
    );
};
